import { useLocation } from "react-router-dom";

/**
 * Returns the active brand identity per page.
 * Used by Header, Footer and any surface that displays the logo + suffix.
 *
 * Premissa: o ícone RSM e o sufixo do logotipo (Politics / Dedicada / ...)
 * assumem a cor da vertical em que o usuário está navegando.
 */
export function useBrand() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/saas")) {
    return { suffix: "Dedicada", color: "#3ED383" };
  }

  if (pathname.startsWith("/campanhas")) {
    return { suffix: "Campanhas", color: "#FA9F22" };
  }

  if (pathname.startsWith("/agentes")) {
    return { suffix: "Network", color: "#7E20FF" };
  }

  // Default: RSM Politics (incluindo /politics, /politics/agencies, /politics/parceiros)
  return { suffix: "Politics", color: "#208BFF" };
}

/**
 * Navegação contextual do Header por rota.
 * Cada rota tem seus próprios anchors válidos (problema/pilares/...) e seu CTA primário
 * apontando para o formulário daquela página.
 */
const NAV_BY_ROUTE = {
  "/politics": {
    base: "/politics",
    items: [
      { id: "problema", label: "O problema" },
      { id: "pilares", label: "Como funciona" },
      { id: "personas", label: "Para quem" },
      { id: "faq", label: "FAQ" },
    ],
    cta: { to: "/politics#contato", label: "Quero RSM" },
    secondary: { to: "/politics/agencies", label: "Sou agência" },
  },
  "/politics/agencies": {
    base: "/politics/agencies",
    items: [
      { id: "como-funciona", label: "Como funciona" },
      { id: "beneficios", label: "Benefícios" },
      { id: "faq", label: "FAQ" },
    ],
    cta: { to: "/politics/agencies#parceria", label: "Quero ser parceiro" },
    secondary: { to: "/politics", label: "RSM Politics" },
  },
  "/politics/parceiros": {
    base: "/politics/parceiros",
    items: [],
    cta: { to: "/politics#contato", label: "Quero RSM" },
    secondary: { to: "/politics", label: "RSM Politics" },
  },
  "/saas": {
    base: "/saas",
    items: [
      { id: "por-que", label: "Por que" },
      { id: "como-funciona", label: "Como funciona" },
      { id: "aplicacoes", label: "Aplicações" },
      { id: "faq", label: "FAQ" },
    ],
    cta: { to: "/saas#diagnostico", label: "Quero diagnóstico" },
    secondary: { to: "/politics", label: "RSM Politics" },
  },
  "/campanhas": {
    base: "/campanhas",
    items: [
      { id: "por-que", label: "Por que" },
      { id: "engagement-media", label: "Como funciona" },
      { id: "usos", label: "Usos" },
      { id: "faq", label: "FAQ" },
    ],
    cta: { to: "/campanhas#piloto", label: "Quero o piloto" },
    secondary: { to: "/politics", label: "RSM Politics" },
  },
  "/agentes": {
    base: "/agentes",
    items: [
      { id: "por-que", label: "Por que" },
      { id: "como-funciona", label: "Como funciona" },
      { id: "missoes", label: "Missões" },
      { id: "beneficios", label: "Benefícios" },
    ],
    cta: { to: "/agentes#cadastro", label: "Quero me cadastrar" },
    secondary: { to: "/politics", label: "RSM Politics" },
  },
};

export function useNavConfig() {
  const { pathname } = useLocation();

  // Match mais específico primeiro (/politics/agencies antes de /politics)
  const keys = Object.keys(NAV_BY_ROUTE).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (pathname === key || pathname.startsWith(`${key}/`)) {
      return NAV_BY_ROUTE[key];
    }
  }
  return NAV_BY_ROUTE["/politics"];
}

/**
 * Logo da RSM que assume a cor da vertical ativa.
 * Usa o SVG como máscara CSS, então qualquer cor é aplicável sem novos arquivos.
 */
export function BrandMark({ size = 32, color, className = "", ...rest }) {
  const { color: defaultColor } = useBrand();
  const finalColor = color || defaultColor;
  return (
    <span
      data-testid="brand-mark"
      role="img"
      aria-label="RSM"
      className={`inline-block flex-shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundColor: finalColor,
        WebkitMaskImage: "url(/rsm-logo.svg)",
        maskImage: "url(/rsm-logo.svg)",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        transition: "background-color 300ms ease",
      }}
      {...rest}
    />
  );
}
